import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Check, X, Pencil, RefreshCw, Mail, Search, RotateCcw } from 'lucide-react';
import { getAccessToken } from '../services/auth';

// Procurement Brain > Vendor Pricing. Price sheets and offers read out of Greg's
// "Vendor Pricing" Outlook folder (netlify/functions/lib/vendor-pricing.js).
//
// ⚠️ NOTHING COUNTS UNTIL SOMEONE TICKS IT. Claude's reading of each email arrives as
// "To confirm"; only confirmed prices show under Current prices and are quoted by Ask.

async function call(action, body = {}) {
  const res = await fetch('/.netlify/functions/vendor-pricing', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
    body: JSON.stringify({ action, ...body }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

const money = (n) => (n == null || n === '' ? 'n/a'
  : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const day = (d) => {
  if (!d) return '';
  const s = String(d).slice(0, 10);
  const dt = new Date(s + 'T12:00:00');
  return Number.isNaN(dt.getTime()) ? s : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const perUnit = (p) => money(p.price) + (p.unit ? ` / ${p.unit}` : '');

const SUBTABS = [
  { key: 'confirm', label: 'To confirm' },
  { key: 'prices', label: 'Current prices' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'emails', label: 'Emails read' },
];

const th = { textAlign: 'left', padding: '6px 8px', fontSize: 12, color: '#5a6b7a', fontWeight: 600, borderBottom: '1px solid #dbe3ea', whiteSpace: 'nowrap' };
const td = { padding: '7px 8px', fontSize: 13, borderBottom: '1px solid #eef2f6', verticalAlign: 'top' };
const input = { width: '100%', fontSize: 13, padding: '3px 5px', border: '1px solid #c9d4de', borderRadius: 4 };

export default function VendorPricing() {
  const [sub, setSub] = useState('confirm');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try { setData(await call('overview')); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (t) => { setToast(t); setTimeout(() => setToast(''), 3500); };
  const act = async (fn, msg) => {
    setBusy(true); setErr('');
    try { await fn(); if (msg) flash(msg); await load(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const checkNow = () => act(async () => {
    await call('check_now');
  }, 'Checking the folder. New emails take about a minute each; refresh in a minute or two.');

  const pendingCount = data ? data.pending.length : 0;

  return (
    <div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Mail size={18} color="var(--navy)" />
          <div style={{ flex: '1 1 320px', fontSize: 13, color: '#3d4f60' }}>
            Put vendor price sheets and offers in the <strong>Vendor Pricing</strong> folder in Greg's Outlook
            (forward them to Greg if they land with you). Claude reads the folder every two hours from 7am to 7pm.
            Nothing it reads counts until someone confirms it here.
          </div>
          <button className="btn btn-outline" onClick={checkNow} disabled={busy}>Check the folder now</button>
          <button className="btn btn-ghost" onClick={load} disabled={busy}><RefreshCw size={14} /></button>
        </div>
      </div>

      {err && <div className="card" style={{ borderLeft: '4px solid #a12020', marginBottom: 14 }}><div className="card-body" style={{ color: '#a12020' }}>{err}</div></div>}
      {toast && <div className="card" style={{ borderLeft: '4px solid #1c6b3f', marginBottom: 14 }}><div className="card-body" style={{ color: '#1c6b3f' }}>{toast}</div></div>}

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {SUBTABS.map(t => {
          const on = sub === t.key;
          const n = t.key === 'confirm' ? pendingCount : null;
          return (
            <button key={t.key} onClick={() => setSub(t.key)} className={on ? 'btn btn-primary' : 'btn btn-outline'} style={{ fontSize: 13 }}>
              {t.label}{n ? ` (${n})` : ''}
            </button>
          );
        })}
      </div>

      {!data && !err && <div className="card"><div className="card-body">Loading…</div></div>}
      {data && sub === 'confirm' && <ToConfirm data={data} act={act} busy={busy} />}
      {data && sub === 'prices' && <CurrentPrices />}
      {data && sub === 'vendors' && <Vendors vendors={data.vendors} act={act} busy={busy} />}
      {data && sub === 'emails' && <Emails sources={data.sources} act={act} busy={busy} />}
    </div>
  );
}

// ── To confirm ────────────────────────────────────────────────────────────────

function ToConfirm({ data, act, busy }) {
  const groups = useMemo(() => {
    const m = new Map();
    for (const p of data.pending) {
      if (!m.has(p.source_id)) m.set(p.source_id, { id: p.source_id, subject: p.subject, received: p.received_at, summary: p.summary, vendor: p.vendor_name, rows: [] });
      m.get(p.source_id).rows.push(p);
    }
    return [...m.values()];
  }, [data.pending]);

  if (!groups.length) {
    return <div className="card"><div className="card-body" style={{ color: '#5a6b7a' }}>Nothing waiting. Every price read so far has been confirmed or set aside.</div></div>;
  }
  return groups.map(g => <ConfirmGroup key={g.id} g={g} act={act} busy={busy} />);
}

function ConfirmGroup({ g, act, busy }) {
  const [editing, setEditing] = useState(null);
  const ids = g.rows.map(r => r.id);
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ flex: '1 1 300px' }}>
            <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{g.vendor}</div>
            <div style={{ fontSize: 12, color: '#5a6b7a' }}>{g.subject} · received {day(g.received)}</div>
            {g.summary && <div style={{ fontSize: 13, marginTop: 4 }}>{g.summary}</div>}
          </div>
          <button className="btn btn-primary" disabled={busy}
            onClick={() => act(() => call('confirm', { ids }), `Confirmed ${ids.length} price${ids.length === 1 ? '' : 's'}.`)}>
            <Check size={14} /> Confirm all {ids.length}
          </button>
          <button className="btn btn-outline" disabled={busy}
            onClick={() => { if (window.confirm(`Set aside all ${ids.length} prices from this email?`)) act(() => call('reject', { ids }), 'Set aside.'); }}>
            Set all aside
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Product</th><th style={th}>Pack</th><th style={th}>Price</th><th style={th}>Qty break</th>
              <th style={th}>From</th><th style={th}>What it said</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {g.rows.map(r => editing === r.id
                ? <EditRow key={r.id} r={r} act={act} done={() => setEditing(null)} />
                : (
                  <tr key={r.id}>
                    <td style={td}><div style={{ fontWeight: 600 }}>{r.product}</div>
                      {(r.category || r.origin || r.note) && <div style={{ fontSize: 12, color: '#5a6b7a' }}>{[r.category, r.origin, r.note].filter(Boolean).join(' · ')}</div>}</td>
                    <td style={td}>{r.pack || ''}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{perUnit(r)}</td>
                    <td style={td}>{r.min_qty || ''}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(r.effective_date)}</td>
                    <td style={{ ...td, fontSize: 12, color: '#5a6b7a', maxWidth: 280 }}>{r.quote}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button className="btn btn-ghost" title="Confirm" disabled={busy} onClick={() => act(() => call('confirm', { ids: [r.id] }))}><Check size={15} color="#1c6b3f" /></button>
                      <button className="btn btn-ghost" title="Fix it" disabled={busy} onClick={() => setEditing(r.id)}><Pencil size={14} /></button>
                      <button className="btn btn-ghost" title="Set aside" disabled={busy} onClick={() => act(() => call('reject', { ids: [r.id] }))}><X size={15} color="#a12020" /></button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EditRow({ r, act, done }) {
  const [f, setF] = useState({
    product: r.product || '', pack: r.pack || '', price: r.price ?? '', unit: r.unit || '',
    min_qty: r.min_qty || '', effective_date: r.effective_date ? String(r.effective_date).slice(0, 10) : '', note: r.note || '',
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = (andConfirm) => act(async () => {
    await call('update_price', { id: r.id, fields: f });
    if (andConfirm) await call('confirm', { ids: [r.id] });
    done();
  }, andConfirm ? 'Fixed and confirmed.' : 'Saved.');
  return (
    <tr style={{ background: '#fbfcfd' }}>
      <td style={td}><input style={input} value={f.product} onChange={set('product')} />
        <input style={{ ...input, marginTop: 4 }} placeholder="note" value={f.note} onChange={set('note')} /></td>
      <td style={td}><input style={input} value={f.pack} onChange={set('pack')} /></td>
      <td style={td}><div style={{ display: 'flex', gap: 4 }}>
        <input style={{ ...input, width: 80 }} type="number" step="0.01" value={f.price} onChange={set('price')} />
        <input style={{ ...input, width: 60 }} placeholder="per" value={f.unit} onChange={set('unit')} /></div></td>
      <td style={td}><input style={input} value={f.min_qty} onChange={set('min_qty')} /></td>
      <td style={td}><input style={input} type="date" value={f.effective_date} onChange={set('effective_date')} /></td>
      <td style={{ ...td, fontSize: 12, color: '#5a6b7a' }}>{r.quote}</td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => save(true)}>Save + confirm</button>{' '}
        <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={done}>Cancel</button>
      </td>
    </tr>
  );
}

// ── Current prices ────────────────────────────────────────────────────────────

function CurrentPrices() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const run = useCallback(async (text) => {
    setErr('');
    try { setRows((await call('prices', { q: text || null })).prices); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { run(''); }, [run]);

  return (
    <div className="card"><div className="card-body">
      <form onSubmit={(e) => { e.preventDefault(); run(q); }} style={{ display: 'flex', gap: 8, marginBottom: 12, maxWidth: 480 }}>
        <input style={{ ...input, fontSize: 14, padding: '6px 8px' }} placeholder="Search a product, vendor or category (lump, caviar, shrimp…)" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn btn-outline" type="submit"><Search size={14} /></button>
      </form>
      {err && <div style={{ color: '#a12020' }}>{err}</div>}
      {rows && !rows.length && <div style={{ color: '#5a6b7a' }}>No confirmed prices{q ? ' match that' : ' yet. Confirm some under To confirm and they show here'}.</div>}
      {rows && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Product</th><th style={th}>Vendor</th><th style={th}>Pack</th><th style={th}>Price</th>
              <th style={th}>Qty break</th><th style={th}>As of</th><th style={th}>Before</th>
            </tr></thead>
            <tbody>
              {rows.map(r => {
                const moved = r.prev_price != null && r.price != null && Number(r.prev_price) !== Number(r.price);
                const up = moved && Number(r.price) > Number(r.prev_price);
                return (
                  <tr key={r.id}>
                    <td style={td}><div style={{ fontWeight: 600 }}>{r.product}</div>
                      {(r.category || r.note) && <div style={{ fontSize: 12, color: '#5a6b7a' }}>{[r.category, r.note].filter(Boolean).join(' · ')}</div>}</td>
                    <td style={td}>{r.vendor_name}</td>
                    <td style={td}>{r.pack || ''}</td>
                    <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>{perUnit(r)}</td>
                    <td style={td}>{r.min_qty || ''}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(r.effective_date)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: moved ? (up ? '#a12020' : '#1c6b3f') : '#5a6b7a' }}>
                      {r.prev_price != null ? `${money(r.prev_price)} on ${day(r.prev_date)}` : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div></div>
  );
}

// ── Vendors ───────────────────────────────────────────────────────────────────

const VFIELDS = [
  ['name', 'Name'], ['sells', 'What they sell'], ['contact_name', 'Contact'], ['contact_email', 'Email'],
  ['contact_phone', 'Phone'], ['how_to_order', 'How to order'], ['minimums', 'Minimums'], ['terms', 'Terms'],
  ['website', 'Website'], ['notes', 'Notes'],
];

function Vendors({ vendors, act, busy }) {
  const [editing, setEditing] = useState(null);
  if (!vendors.length) return <div className="card"><div className="card-body" style={{ color: '#5a6b7a' }}>No vendors yet. They are added as their emails are read.</div></div>;
  return vendors.map(v => (
    <div key={v.vendor_key} className="card" style={{ marginBottom: 12 }}>
      <div className="card-body">
        {editing === v.vendor_key
          ? <VendorForm v={v} act={act} done={() => setEditing(null)} />
          : (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15, flex: '1 1 auto' }}>{v.name}</div>
                <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 10, background: v.status === 'confirmed' ? '#e6f5ec' : '#fff4dd', color: v.status === 'confirmed' ? '#1c6b3f' : '#8a5a00' }}>
                  {v.status === 'confirmed' ? `Confirmed by ${v.confirmed_by || 'staff'}` : 'Draft, not yet confirmed'}
                </span>
                <button className="btn btn-ghost" disabled={busy} onClick={() => setEditing(v.vendor_key)}><Pencil size={14} /> Edit</button>
                {v.status !== 'confirmed' && (
                  <button className="btn btn-outline" disabled={busy} onClick={() => act(() => call('save_vendor', { key: v.vendor_key, fields: {}, confirm: true }), 'Vendor confirmed.')}>
                    <Check size={14} /> Looks right
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '4px 18px', fontSize: 13 }}>
                {VFIELDS.slice(1).filter(([k]) => v[k]).map(([k, label]) => (
                  <div key={k}><span style={{ color: '#5a6b7a' }}>{label}: </span>{v[k]}</div>
                ))}
              </div>
              <div style={{ fontSize: 12, color: '#5a6b7a', marginTop: 6 }}>
                {v.confirmed_prices} confirmed price{v.confirmed_prices === 1 ? '' : 's'}{v.latest_price_date ? `, latest ${day(v.latest_price_date)}` : ''}
              </div>
            </>
          )}
      </div>
    </div>
  ));
}

function VendorForm({ v, act, done }) {
  const [f, setF] = useState(Object.fromEntries(VFIELDS.map(([k]) => [k, v[k] || ''])));
  const save = (confirm) => act(async () => { await call('save_vendor', { key: v.vendor_key, fields: f, confirm }); done(); }, 'Saved.');
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
        {VFIELDS.map(([k, label]) => (
          <label key={k} style={{ fontSize: 12, color: '#5a6b7a' }}>{label}
            <input style={{ ...input, marginTop: 2 }} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </label>
        ))}
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" onClick={() => save(true)}>Save + confirm</button>
        <button className="btn btn-outline" onClick={() => save(false)}>Save</button>
        <button className="btn btn-ghost" onClick={done}>Cancel</button>
      </div>
    </div>
  );
}

// ── Emails read ───────────────────────────────────────────────────────────────

function Emails({ sources, act, busy }) {
  if (!sources.length) return <div className="card"><div className="card-body" style={{ color: '#5a6b7a' }}>No emails read yet. Use "Check the folder now" above.</div></div>;
  const label = { extracted: 'Read', no_prices: 'No prices in it', error: 'Could not read' };
  return (
    <div className="card"><div className="card-body" style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={th}>Email</th><th style={th}>Received</th><th style={th}>Result</th><th style={th}>Prices</th><th style={th}></th>
        </tr></thead>
        <tbody>
          {sources.map(s => (
            <tr key={s.id}>
              <td style={td}><div style={{ fontWeight: 600 }}>{s.subject}</div>
                <div style={{ fontSize: 12, color: '#5a6b7a' }}>{s.from_name}{s.attachment_names && s.attachment_names.length ? ` · ${s.attachment_names.join(', ')}` : ''}</div>
                {s.summary && <div style={{ fontSize: 12, marginTop: 2 }}>{s.summary}</div>}</td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>{day(s.received_at)}</td>
              <td style={{ ...td, color: s.status === 'error' ? '#a12020' : undefined }}>{label[s.status] || s.status}
                {s.detail && <div style={{ fontSize: 12, color: '#a12020' }}>{s.detail}</div>}</td>
              <td style={td}>{s.price_count || 0}{s.pending ? ` (${s.pending} to confirm)` : ''}</td>
              <td style={td}>
                <button className="btn btn-ghost" title="Read it again" disabled={busy}
                  onClick={() => { if (window.confirm('Forget this email and read it again? Prices still waiting to be confirmed from it are replaced.')) act(() => call('forget_source', { id: s.id }), 'Reading it again. Refresh in a minute or two.'); }}>
                  <RotateCcw size={14} /> Read again
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div></div>
  );
}
